import type {
  StockDetailData,
  StockHolderNumberPoint,
  StockHolderTradeRow,
  StockKlineBar,
} from "fin-web-components";
import { useH5Mock } from "../../global/GlobalMock";
import { post } from "../../utils/Backend";
import {
  mockStockDayKlinesByCode,
  mockStockDetailByCode,
  mockStockHolderNumbers,
  mockStockHolderTrades,
} from "./StockMock";

const QUERY_STOCK_DETAIL_URL = "/lark/app/stock/queryStockDetailInfo";
const QUERY_STOCK_DAY_KLINES_URL = "/lark/app/stock/queryStockDayKlines";
const QUERY_HOLDER_NUMBERS_URL = "/lark/app/stock/queryStockHolderNumbers";
const QUERY_HOLDER_TRADES_URL = "/lark/app/stock/queryStockHolderTrades";

export const StockApi = {
  async queryStockDetailInfo(stockCode: string): Promise<StockDetailData | null> {
    const code = stockCode.trim();
    if (!code) {
      return null;
    }
    if (useH5Mock) {
      return mockStockDetailByCode(code);
    }
    const r = await post<{ stockCode: string }, StockDetailData>(QUERY_STOCK_DETAIL_URL, {
      stockCode: code,
    });
    if (typeof r === "string") {
      return null;
    }
    return r;
  },

  async queryStockDayKlines(stockCode: string, size = 300): Promise<StockKlineBar[]> {
    const code = stockCode.trim();
    if (!code) {
      return [];
    }
    if (useH5Mock) {
      return mockStockDayKlinesByCode(code, size);
    }
    const r = await post<{ stockCode: string; size?: number }, StockKlineBar[]>(
      QUERY_STOCK_DAY_KLINES_URL,
      { stockCode: code, size }
    );
    if (typeof r === "string" || !Array.isArray(r)) {
      return [];
    }
    return r;
  },

  async queryStockHolderNumbers(stockCode: string, size = 120): Promise<StockHolderNumberPoint[]> {
    const code = stockCode.trim();
    if (!code) {
      return [];
    }
    if (useH5Mock) {
      return mockStockHolderNumbers(code, size);
    }
    const r = await post<{ stockCode: string; size?: number }, StockHolderNumberPoint[]>(
      QUERY_HOLDER_NUMBERS_URL,
      { stockCode: code, size }
    );
    if (typeof r === "string" || !Array.isArray(r)) {
      return [];
    }
    return r;
  },

  async queryStockHolderTrades(stockCode: string, limit = 20): Promise<StockHolderTradeRow[]> {
    const code = stockCode.trim();
    if (!code) {
      return [];
    }
    if (useH5Mock) {
      return mockStockHolderTrades(code, limit);
    }
    const r = await post<{ stockCode: string; limit?: number }, StockHolderTradeRow[]>(
      QUERY_HOLDER_TRADES_URL,
      { stockCode: code, limit }
    );
    if (typeof r === "string" || !Array.isArray(r)) {
      return [];
    }
    return r.map((row) => ({
      annDate: String(row.annDate ?? ""),
      beginDate: row.beginDate == null || row.beginDate === "" ? null : String(row.beginDate),
      closeDate: row.closeDate == null || row.closeDate === "" ? null : String(row.closeDate),
      holderName: row.holderName,
      inDe: row.inDe,
      changeVol: row.changeVol,
      changeRatio: row.changeRatio,
      avgPrice: row.avgPrice,
    }));
  },
};
